import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { motion } from "framer-motion";

export function Quiz({ lesson, onCorrect }) {
  const multi = !!lesson.multi;
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (id) => {
    if (submitted) return;
    if (multi) {
      setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    } else {
      setSelected([id]);
    }
  };

  const correctIds = lesson.options.filter((o) => o.correct).map((o) => o.id);
  const isCorrect =
    selected.length === correctIds.length && selected.every((id) => correctIds.includes(id));

  const submit = () => {
    if (selected.length === 0) return;
    setSubmitted(true);
    if (isCorrect && onCorrect) onCorrect();
  };

  return (
    <div className="space-y-6">
      <div className="border-l-4 border-primary pl-4 py-2">
        <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          question
        </div>
        <div className="text-lg font-medium" data-testid="quiz-question">{lesson.question}</div>
        {multi && (
          <div className="text-xs text-muted-foreground mt-2 font-mono-code">
            (select all that apply)
          </div>
        )}
      </div>

      <div className="grid gap-3">
        {lesson.options.map((opt, idx) => {
          const isSel = selected.includes(opt.id);
          const showRight = submitted && opt.correct;
          const showWrong = submitted && isSel && !opt.correct;
          return (
            <button
              key={opt.id}
              data-testid={`quiz-option-${opt.id}`}
              onClick={() => toggle(opt.id)}
              disabled={submitted}
              className={
                "flex items-center gap-4 text-left p-4 border-2 transition-colors font-medium " +
                (showRight
                  ? "border-success bg-success/10 text-success"
                  : showWrong
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : isSel
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary")
              }
            >
              <span className="font-mono-code text-xs w-6 h-6 border border-current flex items-center justify-center">
                {String.fromCharCode(65 + idx)}
              </span>
              <span className="flex-1">{opt.label}</span>
              {showRight && <Check className="w-5 h-5" />}
              {showWrong && <X className="w-5 h-5" />}
            </button>
          );
        })}
      </div>

      {!submitted ? (
        <button
          data-testid="quiz-submit-btn"
          onClick={submit}
          disabled={selected.length === 0}
          className="bg-primary text-primary-foreground px-5 py-2.5 font-medium border-2 border-primary hard-shadow-ink disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Check Answer
        </button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={
            "border-2 p-5 " +
            (isCorrect ? "border-success bg-success/5" : "border-primary bg-primary/5")
          }
          data-testid="quiz-explanation"
        >
          <div className="font-mono-code text-[10px] uppercase tracking-widest mb-2">
            {isCorrect ? "correct · +15 xp" : "not quite"}
          </div>
          <div className="text-sm leading-relaxed">{lesson.explain}</div>
        </motion.div>
      )}
    </div>
  );
}
